import { classNames } from 'libs/react';
import { QuoteSummaryPanelStatement } from './QuoteSummaryPanelStatement';
import { SaleItemViewer } from 'components/saleItem/SaleItemViewer';
import { Case } from 'components/util/Case';
import { React } from 'libs/react';
import { getOtherFeeRestrictionQuantity, getOtherFeeRestrictionSubTotal } from 'helpers/otherFeesCalculation';
import { DealItemPanelGroup } from 'modules/deal/components/dealItemPanelGroup/DealItemPanelGroup';
import { useQuoteContext } from './QuoteScreen';
import { Button } from 'components/Button';
import { Text } from 'components/Text';
import { getConfigurationFieldValue } from 'helpers/ConfigurationField';
import './QuoteSummary.scss';
import { useDomain } from 'components/domain/DomainProvider';
/**
 * @component QuoteSummary
 * @since 1.0.0
 */
export function QuoteSummary(props) {
    var _a;
    const { data, discountRep, configurationFields } = props;
    const { updateDealField, deal, updateDealOtherFees } = useQuoteContext();
    const priceDisplay = (_a = deal === null || deal === void 0 ? void 0 : deal.priceDisplay) !== null && _a !== void 0 ? _a : 'ALL';
    const has3DViewer = configurationFields
        ? getConfigurationFieldValue(configurationFields, '3D_VIEWER')
        : null;
    const itemSorting = configurationFields
        ? getConfigurationFieldValue(configurationFields, 'ITEM_SORTING')
        : 'SKU';
    const itemsGrouped = configurationFields
        ? getConfigurationFieldValue(configurationFields, 'ITEM_GROUPED')
        : 'FALSE';
    const hideSKU = configurationFields
        ? getConfigurationFieldValue(configurationFields, 'HIDE_SKU')
        : 'FALSE';
    const defaultSaleItemGroupImage = configurationFields
        ? getConfigurationFieldValue(configurationFields, 'DEFAULT_GROUP_IMAGE', true)
        : null;
    const domain = useDomain();
    let optionSectionLabel = t('Options');
    if (domain.host === 'spaeastman.centralio.com') {
        optionSectionLabel = t('Inclusions');
    }
    /**
     * @function sortOptions
     * @since 1.0.0
     *
     */
    function sortOptions(options) {
        if (itemSorting === 'TITLE') {
            options.sort((a, b) => {
                var _a, _b;
                if (((_a = a.item) === null || _a === void 0 ? void 0 : _a.title) && ((_b = b.item) === null || _b === void 0 ? void 0 : _b.title)) {
                    return a.item.title.localeCompare(b.item.title);
                }
                return 0;
            });
        }
        if (itemSorting === 'SKU') {
            options.sort((a, b) => {
                var _a, _b;
                if (((_a = a.item) === null || _a === void 0 ? void 0 : _a.sku) && ((_b = b.item) === null || _b === void 0 ? void 0 : _b.sku)) {
                    return a.item.sku.localeCompare(b.item.sku);
                }
                return 0;
            });
        }
        if (itemSorting === 'SALE_ITEM_GROUP') {
            options.sort((a, b) => {
                var _a, _b, _c, _d;
                if (((_b = (_a = a.item) === null || _a === void 0 ? void 0 : _a.group) === null || _b === void 0 ? void 0 : _b.title) && ((_d = (_c = b.item) === null || _c === void 0 ? void 0 : _c.group) === null || _d === void 0 ? void 0 : _d.title)) {
                    return a.item.group.title.localeCompare(b.item.group.title);
                }
                return 0;
            });
        }
        if (itemSorting === 'OPTION_TIME') {
            options.sort((a, b) => {
                if (!a.startTime && !b.startTime)
                    return 0;
                if (!a.startTime)
                    return 1;
                if (!b.startTime)
                    return -1;
                return a.startTime.localeCompare(b.startTime);
            });
        }
        return options;
    }
    /**
     * @function filterProposedOptionsOut
     * @since 1.0.0
     * @hidden
     */
    function filterProposedOptionsOut(item) {
        var _a, _b;
        return Object.assign(Object.assign({}, item), { options: sortOptions((_b = (_a = item.options) === null || _a === void 0 ? void 0 : _a.filter((option) => !option.proposed)) !== null && _b !== void 0 ? _b : []) });
    }
    /**
     * @function containProposedOptions
     * @since 1.0.0
     * @hidden
     */
    const containProposedOptions = (item) => {
        var _a;
        const proposedOptionsCounts = (_a = item.options) === null || _a === void 0 ? void 0 : _a.filter((option) => option.proposed).length;
        return !!proposedOptionsCounts;
    };
    /**
     * @function filterProposedPackagesOut
     * @since 1.0.0
     * @hidden
     */
    function filterProposedPackagesOut(item) {
        var _a;
        return Object.assign(Object.assign({}, item), { packages: (_a = item.packages) === null || _a === void 0 ? void 0 : _a.filter((pack) => !pack.proposed) });
    }
    /**
     * @function containProposedPackages
     * @since 1.0.0
     * @hidden
     */
    const containProposedPackages = (item) => {
        var _a;
        const proposedPackagesCounts = (_a = item.packages) === null || _a === void 0 ? void 0 : _a.filter((pack) => pack.proposed).length;
        return !!proposedPackagesCounts;
    };
    /**
     * @function onChangeProposedItem
     * @since 1.0.0
     * @hidden
     */
    const onChangeProposedItem = (item, active) => {
        const itemIndex = deal === null || deal === void 0 ? void 0 : deal.items.findIndex((i) => i.id === item.id);
        if (itemIndex != null && itemIndex > -1) {
            const tempItems = deal ? [...deal.items] : [];
            tempItems[itemIndex] = Object.assign(Object.assign({}, item), { proposed: active });
            updateDealField('items', tempItems, 'array');
        }
    };
    /**
     * @function calculateFeePrice
     * @since 1.0.0
     * @hidden
     */
    const calculateFeePrice = (fee, dealData) => {
        if (!dealData) {
            return 0;
        }
        let price = 0;
        const restrictionValue = JSON.parse(fee.restrictionValue);
        const restrictionExclusion = fee.restrictionExclusion
            ? JSON.parse(fee.restrictionExclusion)
            : undefined;
        if (fee.type === 'PERCENTAGE') {
            price =
                getOtherFeeRestrictionSubTotal(dealData, fee.restriction, restrictionValue, restrictionExclusion) *
                    (fee.value / 100);
        }
        if (fee.type === 'QUANTITY') {
            price =
                getOtherFeeRestrictionQuantity(dealData, fee.restriction, restrictionValue, restrictionExclusion) * fee.value;
        }
        return price;
    };
    /**
     * @function onItemRemove
     * @since 1.0.0
     * @hidden
     */
    const onItemRemove = (item) => {
        var _a;
        const itemIndex = deal === null || deal === void 0 ? void 0 : deal.items.findIndex((i) => i.id === item.id);
        if (itemIndex != null && itemIndex > -1) {
            const items = deal ? [...deal.items] : [];
            items.splice(itemIndex, 1);
            // Update the deal items
            updateDealField('items', items, 'array');
            // Recalculate otherFees based on the new items
            const updatedDeal = deal ? Object.assign(Object.assign({}, deal), { items }) : undefined;
            const recalculatedOtherFees = (_a = deal === null || deal === void 0 ? void 0 : deal.otherFees) === null || _a === void 0 ? void 0 : _a.map((otherFee) => {
                if (otherFee.fee) {
                    // Recalculate the price based on the new items
                    const newPrice = calculateFeePrice(otherFee.fee, updatedDeal);
                    return Object.assign(Object.assign({}, otherFee), { price: newPrice });
                }
                return otherFee;
            });
            // Update otherFees
            if (updateDealOtherFees) {
                updateDealOtherFees(recalculatedOtherFees || []);
            }
        }
    };
    /**
     * @function onItemAlternative
     * @since 1.0.0
     * @hidden
     */
    const onItemAlternative = (item) => {
        const alternativeItem = deal === null || deal === void 0 ? void 0 : deal.items.find((i) => i.id === item.alternativeTo);
        if (alternativeItem) {
            const tempItems = deal ? [...deal.items] : [];
            const itemIndex = tempItems.findIndex((i) => i.id === item.id);
            const alternativeItemIndex = tempItems.findIndex((i) => i.id === alternativeItem.id);
            if (itemIndex != null && itemIndex > -1) {
                tempItems[itemIndex] = Object.assign(Object.assign({}, item), { proposed: false, alternativeTo: null });
            }
            if (alternativeItemIndex != null && alternativeItemIndex > -1) {
                tempItems[alternativeItemIndex] = Object.assign(Object.assign({}, alternativeItem), { proposed: true, alternativeTo: item.id });
            }
            updateDealField('items', tempItems, 'array');
        }
    };
    const className = classNames('quote-summary', props.className);
    return (React.createElement("div", { className: className }, data.items.map((item, index) => {
        var _a, _b, _c, _d, _e, _f, _g;
        let imageSource = ((_b = (_a = item.item.group) === null || _a === void 0 ? void 0 : _a.image) === null || _b === void 0 ? void 0 : _b.url)
            ? (_d = (_c = item.item.group) === null || _c === void 0 ? void 0 : _c.image) === null || _d === void 0 ? void 0 : _d.url
            : (defaultSaleItemGroupImage !== null && defaultSaleItemGroupImage !== void 0 ? defaultSaleItemGroupImage : undefined);
        // check if it's first occurence of the group
        const firstOccurence = data.items.findIndex((i) => { var _a, _b; return ((_a = i.item.group) === null || _a === void 0 ? void 0 : _a.id) === ((_b = item.item.group) === null || _b === void 0 ? void 0 : _b.id); });
        if (item.item.group && firstOccurence !== index) {
            imageSource = undefined;
        }
        // If items are grouped, we group them by the group
        const groupedItems = [];
        const groupedProposedItems = [];
        if (itemsGrouped === 'TRUE') {
            (_e = item.options) === null || _e === void 0 ? void 0 : _e.forEach((option) => {
                var _a, _b, _c, _d, _e;
                // Check if option is recommended by looking in the parent item's options
                const isRecommended = (_c = (_b = (_a = item.item.options) === null || _a === void 0 ? void 0 : _a.find((opt) => { var _a, _b; return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) === ((_b = option.item) === null || _b === void 0 ? void 0 : _b.id); })) === null || _b === void 0 ? void 0 : _b.recommended) !== null && _c !== void 0 ? _c : false;
                if (!option.proposed) {
                    // Group by regular group (no recommended grouping for non-proposed)
                    if ((_d = option.item) === null || _d === void 0 ? void 0 : _d.group) {
                        const groupIndex = groupedItems.findIndex((i) => { var _a, _b, _c; return ((_a = i === null || i === void 0 ? void 0 : i.group) === null || _a === void 0 ? void 0 : _a.id) === ((_c = (_b = option.item) === null || _b === void 0 ? void 0 : _b.group) === null || _c === void 0 ? void 0 : _c.id); });
                        if (groupIndex === -1) {
                            groupedItems.push({
                                group: option.item.group,
                                items: [option]
                            });
                        }
                        else {
                            groupedItems[groupIndex].items.push(option);
                        }
                    }
                    else {
                        const othersIndex = groupedItems.findIndex((i) => i.group === null);
                        if (othersIndex === -1) {
                            groupedItems.push({
                                group: null,
                                items: [option]
                            });
                        }
                        else {
                            groupedItems[othersIndex].items.push(option);
                        }
                    }
                }
                else {
                    // Proposed items - separate recommended into independent group
                    if (isRecommended) {
                        const recommendedIndex = groupedProposedItems.findIndex((i) => i.isRecommended === true);
                        if (recommendedIndex === -1) {
                            // Create independent "Favorites" group at the beginning
                            groupedProposedItems.unshift({
                                group: null,
                                items: [option],
                                isRecommended: true
                            });
                        }
                        else {
                            groupedProposedItems[recommendedIndex].items.push(option);
                        }
                    }
                    else {
                        // Group by regular group for non-recommended proposed options
                        if ((_e = option.item) === null || _e === void 0 ? void 0 : _e.group) {
                            const groupIndex = groupedProposedItems.findIndex((i) => {
                                var _a, _b, _c;
                                return ((_a = i === null || i === void 0 ? void 0 : i.group) === null || _a === void 0 ? void 0 : _a.id) === ((_c = (_b = option.item) === null || _b === void 0 ? void 0 : _b.group) === null || _c === void 0 ? void 0 : _c.id) &&
                                    i.isRecommended !== true;
                            });
                            if (groupIndex === -1) {
                                groupedProposedItems.push({
                                    group: option.item.group,
                                    items: [option],
                                    isRecommended: false
                                });
                            }
                            else {
                                groupedProposedItems[groupIndex].items.push(option);
                            }
                        }
                        else {
                            const othersIndex = groupedProposedItems.findIndex((i) => i.group === null && i.isRecommended !== true);
                            if (othersIndex === -1) {
                                groupedProposedItems.push({
                                    group: null,
                                    items: [option],
                                    isRecommended: false
                                });
                            }
                            else {
                                groupedProposedItems[othersIndex].items.push(option);
                            }
                        }
                    }
                }
            });
        }
        if (itemsGrouped === 'TRUE') {
            groupedItems.sort((a, b) => {
                var _a, _b, _c, _d;
                if (!a.group && !b.group)
                    return 0;
                if (!a.group)
                    return 1;
                if (!b.group)
                    return -1;
                const orderDiff = ((_a = a.group.order) !== null && _a !== void 0 ? _a : 0) - ((_b = b.group.order) !== null && _b !== void 0 ? _b : 0);
                if (orderDiff !== 0)
                    return orderDiff;
                return ((_c = a.group.title) !== null && _c !== void 0 ? _c : '').localeCompare((_d = b.group.title) !== null && _d !== void 0 ? _d : '');
            });
            const recommendedGroup = groupedProposedItems.find((g) => g.isRecommended);
            const nonRecommended = groupedProposedItems
                .filter((g) => !g.isRecommended)
                .sort((a, b) => {
                var _a, _b, _c, _d;
                if (!a.group && !b.group)
                    return 0;
                if (!a.group)
                    return 1;
                if (!b.group)
                    return -1;
                const orderDiff = ((_a = a.group.order) !== null && _a !== void 0 ? _a : 0) - ((_b = b.group.order) !== null && _b !== void 0 ? _b : 0);
                if (orderDiff !== 0)
                    return orderDiff;
                return ((_c = a.group.title) !== null && _c !== void 0 ? _c : '').localeCompare((_d = b.group.title) !== null && _d !== void 0 ? _d : '');
            });
            groupedProposedItems.length = 0;
            if (recommendedGroup)
                groupedProposedItems.push(recommendedGroup);
            groupedProposedItems.push(...nonRecommended);
        }
        return (React.createElement("div", { key: index },
            React.createElement("div", { className: 'quote-summary-heading' +
                    (!imageSource ? ' quote-no-image' : ''), style: { backgroundImage: `url(${imageSource})` } }),
            React.createElement("div", { className: classNames('quote-summary-content', 'quote-summary-content-normal', !imageSource ? 'quote-no-image' : '') },
                React.createElement("div", { id: item.id, key: item.id, className: "quote-section quote-summary-item" },
                    React.createElement("div", { className: "quote-summary-item-heading" },
                        hideSKU === 'FALSE' && item.item.sku,
                        item.alternativeTo && (React.createElement("div", { className: "quote-summary-item-action" },
                            React.createElement(Button, { label: t('Swap'), onPress: () => onItemAlternative(item), style: "outline", picto: "right-left" }))),
                        item.proposed && !item.alternativeTo && (React.createElement("div", { className: "quote-summary-item-action" },
                            React.createElement(Button, { label: t('Add'), onPress: () => onChangeProposedItem(item, false), style: "outline", picto: "plus" }))),
                        item.initiallyProposed &&
                            !item.proposed &&
                            !item.alternativeTo &&
                            !(deal === null || deal === void 0 ? void 0 : deal.items.some((i) => i.alternativeTo === item.id)) && (React.createElement("div", { className: "quote-summary-item-action" },
                            React.createElement(Button, { label: t('Remove'), onPress: () => onChangeProposedItem(item, true), style: "outline", picto: "minus" }))),
                        (deal === null || deal === void 0 ? void 0 : deal.directSale) &&
                            !item.proposed &&
                            deal.items.length > 1 && (React.createElement("div", { className: "quote-summary-item-action" },
                            React.createElement(Button, { label: t('Remove'), onPress: () => onItemRemove(item), style: "outline", picto: "trash-can" })))),
                    React.createElement("div", { className: "quote-summary-item-content" },
                        (index === 0 ||
                            (index >= 1 &&
                                item.item.id !== (deal === null || deal === void 0 ? void 0 : deal.items[index - 1].item.id))) && (React.createElement(SaleItemViewer, { className: "quote-summary-item-sale-item-viewer", item: item.item, variant: (_f = item.variant) !== null && _f !== void 0 ? _f : undefined, isQuote: true, proposed: item.proposed, has3DViewer: has3DViewer, alternativeTo: (deal === null || deal === void 0 ? void 0 : deal.items.find((i) => i.id === item.alternativeTo))
                                ? (_g = deal.items.find((i) => i.id === item.alternativeTo)) === null || _g === void 0 ? void 0 : _g.item.title
                                : null })),
                        React.createElement("div", { className: "deal-item-panel-body" },
                            React.createElement("div", { className: "deal-item-panel-body-section" },
                                React.createElement(DealItemPanelGroup, { type: "item", item: item, editable: false, discountRep: discountRep, deal: data, pricingDisplay: priceDisplay, hideSKU: hideSKU })),
                            item.packages &&
                                item.packages.filter((f) => !f.proposed).length > 0 && (React.createElement("div", { className: "deal-item-panel-body-section" },
                                React.createElement(DealItemPanelGroup, { type: "package", item: filterProposedPackagesOut(item), discountRep: discountRep, editable: false, deal: data, pricingDisplay: priceDisplay, hideSKU: hideSKU }))),
                            itemsGrouped !== 'TRUE' &&
                                item.options &&
                                item.options.filter((f) => !f.proposed).length > 0 && (React.createElement("div", { className: "deal-item-panel-body-section" },
                                React.createElement(DealItemPanelGroup, { type: "option", item: filterProposedOptionsOut(item), discountRep: discountRep, editable: false, deal: data, showIncludedOptions: data.showIncludedOptions, pricingDisplay: priceDisplay, hideSKU: hideSKU, label: optionSectionLabel }))),
                            itemsGrouped === 'TRUE' &&
                                item.options &&
                                item.options.filter((f) => !f.proposed).length > 0 && (React.createElement("div", { className: "deal-item-panel-body-section" }, groupedItems.map((group, index) => {
                                var _a, _b;
                                return (React.createElement(DealItemPanelGroup, { key: index, type: "option", item: Object.assign(Object.assign({}, item), { options: sortOptions(group.items) }), discountRep: discountRep, editable: false, deal: data, showIncludedOptions: data.showIncludedOptions, pricingDisplay: priceDisplay, hideSKU: hideSKU, itemsGrouped: itemsGrouped === 'TRUE', label: (_b = (_a = group.group) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : t('Others') }));
                            })))),
                        (priceDisplay === 'ALL' ||
                            priceDisplay === 'BUNDLED' ||
                            priceDisplay === 'TOTAL') && (React.createElement("div", { className: "quote-summary-panel-foot" },
                            React.createElement(QuoteSummaryPanelStatement, { deal: data, item: item, priceDisplay: priceDisplay }))),
                        React.createElement(Case, { is: containProposedOptions(item) },
                            React.createElement("div", { className: "deal-item-panel-body-section" },
                                itemsGrouped !== 'TRUE' &&
                                    item.options &&
                                    item.options.length > 0 && (React.createElement(DealItemPanelGroup, { type: "optionProposed", item: Object.assign(Object.assign({}, item), { options: sortOptions([...item.options]) }), discountRep: discountRep, editable: false, pricingDisplay: priceDisplay, hideSKU: hideSKU })),
                                itemsGrouped === 'TRUE' &&
                                    groupedProposedItems.length > 0 && (React.createElement("div", { className: "deal-item-panel-body-section" },
                                    React.createElement(Text, { className: "deal-item-panel-body-section-title", value: t('Suggested Options') }),
                                    groupedProposedItems.map((group, index) => {
                                        var _a, _b;
                                        return (React.createElement(DealItemPanelGroup, { key: index, type: "optionProposed", item: Object.assign(Object.assign({}, item), { options: sortOptions(group.items) }), discountRep: discountRep, editable: false, pricingDisplay: priceDisplay, hideSKU: hideSKU, itemsGrouped: itemsGrouped === 'TRUE', label: group.isRecommended
                                                ? t('Favorites')
                                                : ((_b = (_a = group.group) === null || _a === void 0 ? void 0 : _a.title) !== null && _b !== void 0 ? _b : t('Others')) }));
                                    }))))),
                        React.createElement(Case, { is: containProposedPackages(item) },
                            React.createElement("div", { className: "deal-item-panel-body-section" }, item.packages && item.packages.length > 0 && (React.createElement(DealItemPanelGroup, { type: "packageProposed", item: item, discountRep: discountRep, editable: false, pricingDisplay: priceDisplay, hideSKU: hideSKU })))))))));
    })));
}
